// animate_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["animatable"]
  static values = {
    threshold: { type: Number, default: 0.1 },
    once: { type: Boolean, default: true },
    animationPrefix: { type: String, default: "animate__" }
  }

  connect() {
    if (this.hasAnimatableTarget) {
      this.setupObserver()
    } else {
      // If no specific targets, observe all children with animate-on-scroll class
      this.animatableTargets = Array.from(this.element.querySelectorAll('.animate-on-scroll'))
      if (this.animatableTargets.length > 0) {
        this.setupObserver()
      }
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupObserver() {
    const options = {
      threshold: this.thresholdValue,
      rootMargin: '0px 0px -50px 0px' // Start animation slightly before element is fully in view
    }

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), options)

    // Start observing each target
    this.getElementsToAnimate().forEach(element => {
      this.observer.observe(element)
    })
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.animateElement(entry.target)

        // If configured to animate only once, stop observing this element
        if (this.onceValue) {
          this.observer.unobserve(entry.target)
        }
      } else if (!this.onceValue) {
        // If not set to once, remove animation classes when element leaves viewport
        this.deanimateElement(entry.target)
      }
    })
  }

  animateElement(element) {
    // Get animation type from data attribute
    const animation = element.dataset.animation || "fadeIn"
    const delay = element.dataset.animationDelay || ""
    const speed = element.dataset.animationSpeed || ""

    // Add animate.css classes
    element.classList.add("animate__animated", this.animationPrefixValue + animation)

    // Add optional classes
    if (delay) {
      element.classList.add(this.animationPrefixValue + "delay-" + delay)
    }

    if (speed) {
      element.classList.add(this.animationPrefixValue + speed)
    }

    // Add visible class to control opacity if needed
    element.classList.add("visible")
  }

  deanimateElement(element) {
    // Get animation classes
    const animation = element.dataset.animation || "fadeIn"
    const delay = element.dataset.animationDelay || ""
    const speed = element.dataset.animationSpeed || ""

    // Remove animate.css classes
    element.classList.remove("animate__animated", this.animationPrefixValue + animation)

    // Remove optional classes
    if (delay) {
      element.classList.remove(this.animationPrefixValue + "delay-" + delay)
    }

    if (speed) {
      element.classList.remove(this.animationPrefixValue + speed)
    }

    // Remove visible class
    element.classList.remove("visible")
  }

  getElementsToAnimate() {
    return this.hasAnimatableTarget ? this.animatableTargets : this.animatableTargets
  }
}
